/** Settings → Audit Log. */
import { useState } from "react";
import { Panel } from "@/components/surface/Panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DetailDrawer } from "@/components/ui/detail-drawer";
import { Download, Search } from "lucide-react";
import { format } from "date-fns";
import { THead } from "./shared";
import { useAuditLog, auditExportUrl, type AuditEntryDTO } from "@/hooks/security/useEdrAdmin";

export function AuditLogSection() {
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const [resource, setResource] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [selected, setSelected] = useState<AuditEntryDTO | null>(null);

  const filters = {
    actor: actor || undefined,
    action: action || undefined,
    resource: resource || undefined,
    since: since ? new Date(since).toISOString() : undefined,
    until: until ? new Date(until).toISOString() : undefined,
    limit: 200,
  };

  const q = useAuditLog(filters);
  const entries = q.data?.entries ?? [];

  return (
    <Panel
      title={`Audit log${q.data?.total ? ` · ${q.data.total} total` : ""}`}
      actions={
        <>
          <Button asChild size="sm" variant="outline"><a href={auditExportUrl("csv", filters)} target="_blank" rel="noreferrer"><Download className="h-3.5 w-3.5 mr-1" /> CSV</a></Button>
          <Button asChild size="sm" variant="outline"><a href={auditExportUrl("json", filters)} target="_blank" rel="noreferrer"><Download className="h-3.5 w-3.5 mr-1" /> JSON</a></Button>
        </>
      }
      loading={q.isLoading}
    >
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
        <FilterField label="Actor"><Input value={actor} onChange={(e) => setActor(e.target.value)} placeholder="email or system" className="h-8" /></FilterField>
        <FilterField label="Action"><Input value={action} onChange={(e) => setAction(e.target.value)} placeholder="approval.approve" className="h-8" /></FilterField>
        <FilterField label="Resource"><Input value={resource} onChange={(e) => setResource(e.target.value)} placeholder="approval/abc" className="h-8" /></FilterField>
        <FilterField label="From"><Input type="datetime-local" value={since} onChange={(e) => setSince(e.target.value)} className="h-8" /></FilterField>
        <FilterField label="To"><Input type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} className="h-8" /></FilterField>
      </div>

      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">No audit entries match these filters.</p>
      ) : (
        <div className="overflow-x-auto -mx-4 -my-4">
          <table className="w-full text-xs">
            <THead cols={["Time", "Actor", "Action", "Resource"]} />
            <tbody>
              {entries.map((r) => (
                <tr key={r.id} className="border-b border-border/60 hover:bg-muted/40 cursor-pointer" onClick={() => setSelected(r)}>
                  <td className="px-3 py-2 text-muted-foreground tabular-nums whitespace-nowrap">{format(new Date(r.ts), "yyyy-MM-dd HH:mm:ss")}</td>
                  <td className="px-3 py-2">
                    <span className="text-foreground">{r.actor}</span>
                    <span className="ml-2 text-[10px] uppercase text-muted-foreground">{r.actorType}</span>
                  </td>
                  <td className="px-3 py-2"><code className="font-mono text-[11px]">{r.action}</code></td>
                  <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">{r.resourceType}/{r.resourceId.slice(0, 12)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DetailDrawer open={!!selected} onOpenChange={(o) => !o && setSelected(null)}
        title={selected?.action ?? "Audit entry"} description={selected?.actor}>
        {selected && <AuditDetail entry={selected} />}
      </DetailDrawer>
    </Panel>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-1"><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>;
}

function AuditDetail({ entry }: { entry: AuditEntryDTO }) {
  return (
    <div className="space-y-4 text-xs">
      <Section label="Event">
        <Kv k="Time" v={format(new Date(entry.ts), "yyyy-MM-dd HH:mm:ss")} />
        <Kv k="Actor" v={`${entry.actor} (${entry.actorType})`} />
        <Kv k="Action" v={<code className="font-mono">{entry.action}</code>} />
        <Kv k="Resource" v={<code className="font-mono">{entry.resourceType}/{entry.resourceId}</code>} />
        <Kv k="Source IP" v={entry.sourceIp} />
        <Kv k="Request ID" v={entry.requestId && <code className="font-mono">{entry.requestId}</code>} />
        {entry.reason && <Kv k="Reason" v={entry.reason} />}
      </Section>
      {(entry.before || entry.after) && (
        <Section label="Diff">
          {entry.before != null && (<><Label className="text-[10px] uppercase text-muted-foreground">Before</Label>
            <pre className="rounded border border-border bg-muted/40 p-2 text-[10px] overflow-x-auto whitespace-pre-wrap break-all">{JSON.stringify(entry.before, null, 2)}</pre></>)}
          {entry.after != null && (<><Label className="text-[10px] uppercase text-muted-foreground mt-2">After</Label>
            <pre className="rounded border border-border bg-muted/40 p-2 text-[10px] overflow-x-auto whitespace-pre-wrap break-all">{JSON.stringify(entry.after, null, 2)}</pre></>)}
        </Section>
      )}
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">{label}</h3><div className="rounded border border-border divide-y divide-border">{children}</div></div>;
}
function Kv({ k, v }: { k: string; v?: React.ReactNode }) {
  return <div className="flex items-start justify-between gap-4 px-3 py-2"><span className="text-muted-foreground">{k}</span><span className="text-foreground text-right break-all">{v || "—"}</span></div>;
}
